// VENDORED FROM FLIGHT DECK — DO NOT EDIT (ATC, Hotfix 053).
//
// Byte-copied from `flightdeck:frontend/src/components/FlightDeckLoader.tsx`
// @ 29848e6. Same producer/consumer discipline `ATC-ADR-018` establishes for
// `docs/vendor/rubix-ui-tokens.json`: on divergence flightdeck wins and ATC
// re-vendors. The only local addition is this header.
//
// WHY IT IS USED HERE, against its own guidance. The comment below says to use
// it only "where the wait plausibly reaches" the 2.8s loop. Issuing an
// environment on ATC's HA-off Consumption plan MEASURED 13,000 ms on a cold
// start (App Insights, 2026-09-10 15:14:39) and ~1,550 ms warm. That is a wait
// with nothing to show, and it is exactly the case this asset is for.
//
// It is NOT wired as a per-navigation overlay, per the same guidance: the lists
// keep their existing inline loading state.

// Vendored from the FlightDeck asset kit (~/Downloads/flightdeck-asset-kit,
// reviewed 2026-08-11): the approved runway / trajectory / observability brand
// loader. Self-contained inline SVG — no external requests, includes
// prefers-reduced-motion handling (animation collapses to the static mark).
// Adaptation from the kit original: dropped the unused `import React` (this
// repo uses the automatic JSX runtime); everything else is verbatim.
//
// FD-276 — WHERE THIS BELONGS, AND WHERE IT DOES NOT.
//
// This is for a wait with NOTHING TO SHOW. It is deliberately NOT wired as a
// per-navigation overlay: every list and detail page already renders skeleton
// rows while its query is in flight, and an overlay would COVER that skeleton —
// replacing a preview of the page's actual structure with a logo. That is less
// information, not more, and it would make fast navigations flash.
//
// The one genuinely blank window in this app is app boot, before MSAL resolves
// and anything at all is mounted. That is what BootSplash uses it for.
//
// A 2.8s loop is a long cycle. Use it only where the wait plausibly reaches it;
// for anything shorter, a skeleton is the better answer.
//
// 2.8s loop: runway edges draw (0–18%) → aircraft flies the corridor
// (18–68%) → the four-panel mark resolves (78–92%). Brand gradient
// #FFC107 → #EA7400 → #F0441D, navy #08162B aircraft. The mark's negative
// space is hardcoded #F4F2EE (warm brand canvas) — surfaces showing this
// loader should use that background so the resolve state renders seamlessly.

/**
 * The loader's mark has HARDCODED negative space in this colour, so a surface
 * showing it must paint this exact background or the resolve state renders with
 * a visible seam. It is deliberately NOT `SURFACE.canvas` (#F5F3F0) — the asset
 * kit's value is #F4F2EE and the asset is the authority for its own artwork.
 * Exported so the mismatch is a named constant rather than a magic hex someone
 * later "corrects" to the canvas token.
 */
export const LOADER_CANVAS = '#F4F2EE';

type FlightDeckLoaderProps = {
  size?: number;
  className?: string;
  label?: string;
};

export function FlightDeckLoader({
  size = 96,
  className,
  label = 'Loading FlightDeck',
}: FlightDeckLoaderProps) {
  return (
    <div className={className} role="status" aria-label={label}>
      <svg width={size} height={size} viewBox="0 0 256 256">
        <defs>
          <linearGradient id="fd-gradient" x1="128" y1="24" x2="128" y2="230" gradientUnits="userSpaceOnUse">
            <stop stopColor="#FFC107" />
            <stop offset=".52" stopColor="#EA7400" />
            <stop offset="1" stopColor="#F0441D" />
          </linearGradient>
        </defs>
        <style>{`
          .fd-path { fill:none; stroke:url(#fd-gradient); stroke-width:5; stroke-linecap:round;
            stroke-dasharray:230; animation:fdDraw 2.8s ease-in-out infinite; }
          .fd-center { fill:none; stroke:url(#fd-gradient); stroke-width:3; stroke-dasharray:7 10;
            animation:fdCenter 2.8s ease infinite; }
          .fd-plane { fill:#08162B; transform-box:fill-box; transform-origin:center;
            animation:fdFly 2.8s cubic-bezier(.45,0,.25,1) infinite; }
          .fd-mark { fill:url(#fd-gradient); animation:fdResolve 2.8s ease infinite; }
          @keyframes fdDraw { 0%{stroke-dashoffset:230;opacity:.15} 18%,78%{stroke-dashoffset:0;opacity:1} 100%{stroke-dashoffset:0;opacity:0} }
          @keyframes fdCenter { 0%,12%{opacity:0} 25%,78%{opacity:.75} 100%{opacity:0} }
          @keyframes fdFly { 0%,18%{transform:translateY(82px);opacity:0} 25%{opacity:1} 68%{transform:translateY(-45px);opacity:1} 80%,100%{transform:translateY(-45px);opacity:0} }
          @keyframes fdResolve { 0%,65%{opacity:0} 78%,92%{opacity:1} 100%{opacity:0} }
          @media (prefers-reduced-motion: reduce) {
            .fd-path,.fd-center,.fd-plane,.fd-mark { animation:none; }
            .fd-path,.fd-center,.fd-plane { opacity:0; }
            .fd-mark { opacity:1; }
          }
        `}</style>
        <path className="fd-path" d="M55 215 83 55M201 215 173 55" />
        <path className="fd-center" d="M128 220V48" />
        <g className="fd-plane"><path d="M128 92c3 0 5 3 5 7v27l25 17v8l-25-7v20l9 7v7l-14-4-14 4v-7l9-7v-20l-25 7v-8l25-17V99c0-4 2-7 5-7Z"/></g>
        <g className="fd-mark">
          <path d="M42 78 91 43v151l-49 29V78Zm58-42 20-14v150l-20 16V36Zm114 42-49-35v151l49 29V78Zm-58-42-20-14v150l20 16V36Z"/>
          <path fill="#F4F2EE" d="M128 73c3 0 5 3 5 7v36l27 18v8l-27-8v27l10 8v7l-15-4-15 4v-7l10-8v-27l-27 8v-8l27-18V80c0-4 2-7 5-7Z"/>
          <path d="M125 174h6v55h-6z"/>
        </g>
      </svg>
    </div>
  );
}
